import type { FC } from "react";
import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import Sentry from "@similarweb/sentry-logger";
import { FlexRow, Tab, TabList, TabPanel, Tabs } from "@similarweb/ui-components";

import { isPaidKwSearchAvailable } from "app/pages/keyword-research/utils/permissions";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { keywordGroupsTransforms } from "app/services/keyword-groups";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";
import { webSourceToDeviceForApi } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { SourceTypeMap, EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import type { Filter } from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TopCompetitorsWrapper, TableContainerWrapper } from "./TopCompetitorsStyledComponents";
import { TopCompetitorsTable } from "./TopCompetitorsTable";
import type { ITopCompetitorsResponseData, ITopCompetitorsTableData, DataType } from "./topCompetitorsTypes";
import { DATA_TYPES } from "./topCompetitorsTypes";
import { allTrackers } from "app/services/track/track";

const TOP_COMPETITORS_ENDPOINT = "api/KeywordAnalysis/Overview/TopSites";
const TOP_COMPETITORS_PAGES_ENDPOINT = "api/KeywordAnalysis/Overview/TopPages";
const WIDGET_TITLE_KEY = "keyword.analysis.overview.page.v2.top_competitors.title";
const WIDGET_TOOLTIP_KEY = "keyword.analysis.overview.page.v2.top_competitors.tooltip";

const transformTopCompetitorsData = (
    rawData: ITopCompetitorsResponseData,
    type: DataType,
): ITopCompetitorsTableData => {
    if (!rawData || !rawData[type] || !Array.isArray(rawData[type])) {
        return { Data: [] };
    }
    const transformedDataRows = rawData[type].map((competitor) => {
        const normalizedCompetitor = {
            ...competitor,
            Competitor: competitor.Url || competitor.Domain,
            Organic: competitor.OrganicShare,
            Paid: competitor.PaidShare,
        };
        return normalizedCompetitor;
    });
    return { Data: transformedDataRows };
};

interface ITopCompetitors {
    subtitleFilters: Filter[];
}

export const TopCompetitors: FC<ITopCompetitors> = ({ subtitleFilters = [] }) => {
    const { params } = useCurrentStateAndParams();
    const { keyword, duration, country, webSource } = params;
    const [isLoading, setIsLoading] = useState(true);
    const [pagesIsLoading, setPagesIsLoading] = useState(true);
    const [selectedTab, setSelectedTab] = useState(EKeywordTabs[0]);
    const [tableData, setTableData] = useState<ITopCompetitorsTableData>({ Data: [] });
    const [pagesTableData, setPagesTableData] = useState<ITopCompetitorsTableData>({ Data: [] });

    const fetchData = async (
        endpoint: string,
        setData: (data: ITopCompetitorsTableData) => void,
        setIsLoading: (loading: boolean) => void,
    ) => {
        setIsLoading(true);
        const durationObject = DurationService.getDurationData(duration);
        const { from, to, isWindow } = durationObject.forAPI;
        const apiParams = {
            to,
            from,
            country,
            isWindow,
            device: webSourceToDeviceForApi[webSource],
            key: keywordGroupsTransforms.getGroupIdFromUrlParam(keyword),
            sourceType: SourceTypeMap[selectedTab],
        };
        try {
            const responseData = await DefaultFetchService.getInstance().get<ITopCompetitorsResponseData>(
                endpoint,
                apiParams,
            );

            const transformedData = transformTopCompetitorsData(
                responseData,
                endpoint === TOP_COMPETITORS_ENDPOINT ? DATA_TYPES.COMPETITORS : DATA_TYPES.PAGES,
            );
            setData(transformedData);
        } catch (error) {
            Sentry.error(`Error fetching top competitors data for keyword analysis overview`, error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData(TOP_COMPETITORS_ENDPOINT, setTableData, setIsLoading);
        fetchData(TOP_COMPETITORS_PAGES_ENDPOINT, setPagesTableData, setPagesIsLoading);
    }, [selectedTab]);

    const link = proNavigator.href("organicsearch_keywordAnalysis_2", {
        keyword,
        mtd: false,
        webSource,
        selectedPageTab: selectedTab,
        country,
        duration,
    });

    const pagesLink = proNavigator.href("organicsearch_trafficAnalysis_2", {
        keyword,
        mtd: false,
        webSource,
        selectedPageTab: selectedTab,
        country,
        duration,
    });

    const footerCTA = {
        type: buttonTypes.FLAT,
        text: translate("keyword.analysis.overview.page.v2.top_competitors.cta"),
        link,
        onClick: () => {
            allTrackers.dispatchTrackEvent("Internal Link", "click", "TopCompetitors.Keywords/see_more");
        },
    };

    const footerSerpCTA = {
        type: buttonTypes.FLAT,
        text: translate("keyword.analysis.overview.page.v2.top_competitors.website.cta"),
        link,
    };

    const footerCTAPages = {
        type: buttonTypes.FLAT,
        text: translate("keyword.analysis.overview.page.v2.top_competitors.pages.cta"),
        link: pagesLink,
        onClick: () => {
            allTrackers.dispatchTrackEvent(
                "keyword.analysis.overview.page.v2.top_competitors.pages.cta",
                "click",
                "keyword.analysis.overview.page.v2.top_competitors.pages.cta",
            );
        },
    };

    const onTabSelect = (index) => setSelectedTab(EKeywordTabs[index]);

    return (
        <TopCompetitorsWrapper data-automation="TopCompetitors">
            <WidgetContainer
                title={translate(WIDGET_TITLE_KEY)}
                tooltip={translate(WIDGET_TOOLTIP_KEY)}
                subtitleFilters={subtitleFilters}
                isLoading={false}
                loaderWidth={"100%"}
                loaderHeight={132}
                noData={false}
                withBorder={false}
                noDataTitle={translate("common.noresults")}
                noDataSubtitle={translate("keyword.analysis.overview.page.v2.trend_over_time.noData.subtitle")}
                minHeight={260}
                footerButtonsConfig={[footerSerpCTA, footerCTAPages]}
            >
                <Tabs onSelect={onTabSelect}>
                    <TabList>
                        <Tab id={EKeywordTabs.Total}>
                            {translate("keyword.analysis.overview.page.v2.top_competitors.tab.total")}
                        </Tab>

                        <Tab id={EKeywordTabs.Organic}>
                            {translate("keyword.analysis.overview.page.v2.top_competitors.tab.organic")}
                        </Tab>
                        {isPaidKwSearchAvailable() ? (
                            <Tab id={EKeywordTabs.Paid}>
                                {translate("keyword.analysis.overview.page.v2.top_competitors.tab.paid")}
                            </Tab>
                        ) : null}
                    </TabList>

                    <TabPanel>
                        <FlexRow>
                            <TableContainerWrapper>
                                <TopCompetitorsTable
                                    tab={EKeywordTabs.Total}
                                    tableData={tableData}
                                    isLoading={isLoading}
                                    isWebsite={true}
                                />
                            </TableContainerWrapper>
                            <TableContainerWrapper>
                                <TopCompetitorsTable
                                    tab={EKeywordTabs.Total}
                                    tableData={pagesTableData}
                                    isLoading={pagesIsLoading}
                                />
                            </TableContainerWrapper>
                        </FlexRow>
                    </TabPanel>
                    <TabPanel>
                        <FlexRow>
                            <TableContainerWrapper>
                                <TopCompetitorsTable
                                    tab={EKeywordTabs.Organic}
                                    tableData={tableData}
                                    isLoading={isLoading}
                                    isWebsite={true}
                                />
                            </TableContainerWrapper>
                            <TableContainerWrapper>
                                <TopCompetitorsTable
                                    tab={EKeywordTabs.Organic}
                                    tableData={pagesTableData}
                                    isLoading={pagesIsLoading}
                                />
                            </TableContainerWrapper>
                        </FlexRow>
                    </TabPanel>
                    <TabPanel>
                        <FlexRow>
                            <TableContainerWrapper>
                                <TopCompetitorsTable
                                    tab={EKeywordTabs.Paid}
                                    tableData={tableData}
                                    isLoading={isLoading}
                                    isWebsite={true}
                                />
                            </TableContainerWrapper>
                            <TableContainerWrapper>
                                <TopCompetitorsTable
                                    tab={EKeywordTabs.Paid}
                                    tableData={pagesTableData}
                                    isLoading={pagesIsLoading}
                                />
                            </TableContainerWrapper>
                        </FlexRow>
                    </TabPanel>
                </Tabs>
            </WidgetContainer>
        </TopCompetitorsWrapper>
    );
};
